<!-- Шаг повреждений: вопрос, создание одной или нескольких пометок и итоговый список. -->
<template>
  <VehicleInspectionStepLayout
    v-if="phase === 'question'"
    :title="step.name"
    :description="step.description"
    centered
  >
      <div v-if="knownRemarks.length" class="inspection-remarks__known">
        <base-button
          class="inspection-remarks__known-toggle"
          variant="ghost"
          :right-icon="knownRemarksExpanded ? 'chevronUp' : 'chevronRight'"
          :aria-expanded="knownRemarksExpanded"
          @click="knownRemarksExpanded = !knownRemarksExpanded"
        >
          Уже отмечено по машине: {{ knownRemarks.length }}
        </base-button>
        <div v-if="knownRemarksExpanded" class="inspection-remarks__known-list">
          <RecordListSelectableCard
            v-for="remark in knownRemarks"
            :key="remark.id"
            :title="remark.name || remark.text"
            @select="openKnownRemark(remark)"
          >
            <template #badges><VehicleRemarkBadges :remark="remark" /></template>
            <template #meta>{{ remark.created_by?.initials || "Пользователь" }} · {{ formatDate(remark.created_at) }}</template>
          </RecordListSelectableCard>
          <base-button class="inspection-remarks__all-link" variant="ghost" right-icon="chevronRight" @click="$emit('open-remarks')">Все пометки ТС</base-button>
        </div>
      </div>
      <template #actions>
        <base-primary-button size="large" :disabled="disabled" @click="finishWithoutRemarks">Нет, всё в порядке</base-primary-button>
        <base-button size="large" :disabled="disabled" @click="startAdding">Да, есть</base-button>
      </template>
  </VehicleInspectionStepLayout>

  <VehicleInspectionStepLayout
    v-else-if="phase === 'edit'"
    :title="editingId ? 'Изменить пометку' : 'Что не так?'"
  >
      <base-textarea v-model="text" placeholder="Опишите повреждение или неисправность" />
      <AttachmentPhotoPicker
        :label="file ? file.name : 'Добавить фото'"
        :disabled="disabled"
        @file-selected="selectFile"
      />
      <template #actions>
        <base-primary-button size="large" :disabled="disabled || !text.trim()" @click="saveRemark">Сохранить</base-primary-button>
        <base-button size="large" :disabled="disabled" @click="cancelEditing">Отмена</base-button>
      </template>
  </VehicleInspectionStepLayout>

  <VehicleInspectionStepLayout v-else title="Ещё что-то?" description="Нажмите на пометку, чтобы поправить её.">
      <div class="inspection-remarks__list">
        <RecordListSelectableCard
          v-for="remark in remarks"
          :key="remark.id"
          :title="remark.name || remark.text"
          :description="remark.text"
          @select="editRemark(remark)"
        >
          <template #badges><VehicleRemarkBadges :remark="remark" /></template>
          <template #meta>Изменить</template>
        </RecordListSelectableCard>
      </div>
      <template #actions>
        <base-primary-button size="large" :disabled="disabled" @click="finishWithRemarks">Нет, дальше</base-primary-button>
        <base-button size="large" :disabled="disabled" @click="startAdding">Добавить ещё</base-button>
      </template>
  </VehicleInspectionStepLayout>
</template>

<script>
import AttachmentPhotoPicker from "@/components/Attachment/PhotoPicker.vue";
import RecordListSelectableCard from "@/components/RecordList/SelectableCard.vue";
import VehicleInspectionStepLayout from "@/components/Vehicle/Inspection/Step/Layout.vue";
import VehicleRemarkBadges from "@/components/Vehicle/Remark/Shared/Badges.vue";
import { useAttachmentStore } from "@/stores/attachmentStore";
import { useFleetVehicleInspectionStore } from "@/stores/fleetVehicleInspectionStore";
import { useFleetVehicleRemarkStore } from "@/stores/fleetVehicleRemarkStore";
import { formatDateAndTimeLocal } from "@/utils/dateUtils";

export default {
  name: "VehicleInspectionStepRemarks",
  components: { AttachmentPhotoPicker, RecordListSelectableCard, VehicleInspectionStepLayout, VehicleRemarkBadges },
  props: {
    step: { type: Object, required: true },
    answer: { type: Object, default: null },
    inspection: { type: Object, required: true },
    vehicleId: { type: [Number, String], required: true },
    disabled: { type: Boolean, default: false },
  },
  emits: ["complete", "open-remarks"],
  data() {
    return {
      phase: (this.inspection.participants || []).some((participant) => participant.remarks?.length)
        ? "list"
        : "question",
      text: "",
      file: null,
      editingId: null,
      knownRemarksExpanded: false,
    };
  },
  computed: {
    attachmentStore() { return useAttachmentStore(); },
    remarkStore() { return useFleetVehicleRemarkStore(); },
    store() { return useFleetVehicleInspectionStore(); },
    remarks() {
      const currentInspection = String(this.store.currentInspection?.id) === String(this.inspection.id)
        ? this.store.currentInspection
        : this.inspection;
      return this.store.remarksForInspection(currentInspection);
    },
    knownRemarks() {
      return this.remarkStore.inspectionActiveRemarks.filter((remark) => String(remark.inspection_id) !== String(this.inspection.id));
    },
  },
  created() {
    this.remarkStore.fetchInspectionActiveRemarks(this.vehicleId);
  },
  methods: {
    formatDate: formatDateAndTimeLocal,
    finishWithoutRemarks() { this.$emit("complete", { value: { has_remarks: false, remark_ids: [] } }); },
    finishWithRemarks() { this.$emit("complete", { value: { has_remarks: true, remark_ids: this.remarks.map((remark) => remark.id) } }); },
    openKnownRemark(remark) { this.$emit("open-remarks", remark.id); },
    startAdding() { this.text = ""; this.file = null; this.editingId = null; this.phase = "edit"; },
    editRemark(remark) { this.text = remark.text || ""; this.file = null; this.editingId = remark.id; this.phase = "edit"; },
    cancelEditing() { this.text = ""; this.file = null; this.editingId = null; this.phase = this.remarks.length ? "list" : "question"; },
    selectFile(file) { this.file = file; },
    async saveRemark() {
      const attachmentIds = [];
      if (this.file) attachmentIds.push((await this.attachmentStore.uploadAttachment(this.file)).id);
      const payload = {
        name: this.text.trim().split(/\s+/).slice(0, 8).join(" ").slice(0, 80),
        text: this.text.trim(),
        attachment_ids: attachmentIds,
      };
      if (this.editingId) {
        if (!this.file) delete payload.attachment_ids;
        await this.remarkStore.updateInspectionRemark(this.vehicleId, this.editingId, payload);
      } else {
        await this.remarkStore.createInspectionRemark(this.vehicleId, this.inspection.id, {
          ...payload,
          remark_type: "defect",
          severity: "important",
        });
      }
      await this.store.fetchInspection(this.vehicleId, this.inspection.id);
      this.phase = "list";
      this.text = "";
      this.file = null;
      this.editingId = null;
    },
  },
};
</script>

<style scoped>
.inspection-remarks__list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}

.inspection-remarks__known {
  border: 1px solid var(--color-grey);
  border-radius: 10px;
  overflow: hidden;
}

.inspection-remarks__known-toggle {
  width: 100%;
  min-height: 48px;
  height: auto;
  padding: 10px 14px;
  border: 0;
  background: color-mix(in srgb, var(--color-light) 94%, var(--color-grey));
  color: color-mix(in srgb, var(--color-dark) 78%, var(--color-light));
  justify-content: space-between;
  font-weight: 600;
}

.inspection-remarks__known-list {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inspection-remarks__all-link {
  align-self: flex-start;
  color: var(--color-green);
  text-decoration: underline;
}
</style>
